// Go High Level CRM API Service

import {
  GHLConfig,
  GHLContact,
  GHLOpportunity,
  GHLNote,
  GHLTask,
  GHLApiResponse,
  GHLContactResponse,
  GHLOpportunityResponse,
  ContactFormSubmission,
  NewsletterSignup,
  // ServiceRequestForm, // TODO: Implement service request form handler
  // EventRegistration,   // TODO: Implement event registration handler
  FormSubmissionResult,
  CRMEvent
} from '../types/gohighlevel';

class GoHighLevelService {
  private config: GHLConfig;
  private baseHeaders: Record<string, string>;

  constructor() {
    this.config = {
      apiUrl: process.env.REACT_APP_GHL_API_URL || 'https://rest.gohighlevel.com/v1',
      apiKey: process.env.REACT_APP_GHL_API_KEY || '',
      locationId: process.env.REACT_APP_GHL_LOCATION_ID || '',
      defaultPipelineId: process.env.REACT_APP_GHL_PIPELINE_ID || ''
    };

    this.baseHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config.apiKey}`,
      'Version': '2021-07-28'
    };

    // Log configuration status (without exposing keys)
    if (process.env.NODE_ENV === 'development') {
      console.log('GHL Service Configuration:', {
        apiUrl: this.config.apiUrl,
        hasApiKey: !!this.config.apiKey,
        hasLocationId: !!this.config.locationId,
        hasPipelineId: !!this.config.defaultPipelineId,
        isConfigured: this.isConfigured()
      });
    }
  }

  // Check if the service is properly configured
  isConfigured(): boolean {
    return !!(
      this.config.apiKey &&
      this.config.locationId &&
      this.config.defaultPipelineId
    );
  }

  // Generic API call method
  private async apiCall<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any
  ): Promise<GHLApiResponse<T>> {
    if (!this.isConfigured()) {
      return {
        success: false,
        error: 'Go High Level API not configured properly'
      };
    }

    try {
      const url = `${this.config.apiUrl}${endpoint}`;
      const response = await fetch(url, {
        method,
        headers: this.baseHeaders,
        body: body ? JSON.stringify(body) : undefined
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || `HTTP ${response.status}: ${response.statusText}`,
          data
        };
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Contact Management
  async createContact(contact: GHLContact): Promise<GHLApiResponse<GHLContactResponse>> {
    const contactData = {
      ...contact,
      locationId: this.config.locationId
    };

    return this.apiCall<GHLContactResponse>('/contacts/', 'POST', contactData);
  }

  async getContact(contactId: string): Promise<GHLApiResponse<GHLContactResponse>> {
    return this.apiCall<GHLContactResponse>(`/contacts/${contactId}`);
  }

  async updateContact(contactId: string, updates: Partial<GHLContact>): Promise<GHLApiResponse<GHLContactResponse>> {
    return this.apiCall<GHLContactResponse>(`/contacts/${contactId}`, 'PUT', updates);
  }

  async searchContacts(email: string): Promise<GHLApiResponse<GHLContactResponse[]>> {
    return this.apiCall<GHLContactResponse[]>(`/contacts/?email=${encodeURIComponent(email)}`);
  }

  // Opportunity Management
  async createOpportunity(opportunity: GHLOpportunity): Promise<GHLApiResponse<GHLOpportunityResponse>> {
    // For v1 API, try the direct opportunities endpoint
    const opportunityData = {
      ...opportunity,
      locationId: this.config.locationId
    };
    
    return this.apiCall<GHLOpportunityResponse>(`/opportunities/`, 'POST', opportunityData);
  }

  async updateOpportunity(opportunityId: string, updates: Partial<GHLOpportunity>): Promise<GHLApiResponse<GHLOpportunityResponse>> {
    // Note: For v1 API, you may need the contactId to update opportunities
    // This endpoint may need adjustment based on your specific use case
    return this.apiCall<GHLOpportunityResponse>(`/opportunities/${opportunityId}`, 'PUT', updates);
  }

  // Notes and Tasks
  async addNote(note: GHLNote): Promise<GHLApiResponse> {
    // For v1 API, notes might use a different format or endpoint
    // Try the direct notes endpoint first
    const noteData = {
      contactId: note.contactId,
      body: note.body,
      userId: note.userId || 'system'
    };
    
    // Try the main notes endpoint for v1 API
    return this.apiCall(`/notes/`, 'POST', noteData);
  }

  async createTask(task: GHLTask): Promise<GHLApiResponse> {
    // For v1 API, tasks might be under contacts or a different endpoint
    // This may need adjustment based on testing
    return this.apiCall(`/contacts/${task.contactId}/tasks`, 'POST', task);
  }

  // Form Submission Handlers
  async submitContactForm(formData: ContactFormSubmission): Promise<FormSubmissionResult> {
    const timestamp = new Date().toISOString();
    const enableNotes = (process.env.REACT_APP_GHL_ENABLE_NOTES || 'false').toLowerCase() === 'true';
    const enableOpportunities = (process.env.REACT_APP_GHL_ENABLE_OPPORTUNITIES || 'false').toLowerCase() === 'true';
    
    try {
      // First, check if contact already exists
      const existingContact = await this.searchContacts(formData.email);
      
      let contactId: string;
      
                    if (existingContact.success && existingContact.data && existingContact.data.length > 0) {
         // Update existing contact - handle v1 API response structure
         const v1Contact = existingContact.data[0] as any;
         contactId = v1Contact.contact?.id || v1Contact.id;
        
        const updateData: Partial<GHLContact> = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          companyName: formData.company,
          source: formData.source,
          tags: ['website_lead', 'contact_form_updated']
        };
        
        await this.updateContact(contactId, updateData);
      } else {
        // Create new contact
        const newContact: GHLContact = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.company,
          source: formData.source,
          tags: ['website_lead', 'contact_form'],
          customFields: {
            initial_message: formData.message,
            service_interest: formData.serviceInterest,
            budget_range: formData.budget,
            project_timeline: formData.timeline
          }
        };
        
        const contactResult = await this.createContact(newContact);
        
        if (!contactResult.success) {
          return {
            success: false,
            error: contactResult.error || 'Failed to create contact',
            timestamp
          };
        }
        
        // Debug: Log the contact creation response structure
        if (process.env.NODE_ENV === 'development') {
          console.log('Contact creation response:', contactResult);
        }
        
        // Handle different response structures for contact ID
        // GoHighLevel v1 API returns contact ID in data.contact.id
        const v1Response = contactResult.data as any;
        if (v1Response?.contact?.id) {
          contactId = v1Response.contact.id;
        } else if (contactResult.data?.id) {
          contactId = contactResult.data.id;
        } else if (typeof contactResult.data === 'string') {
          contactId = contactResult.data;
        } else {
          console.warn('Contact created but no ID returned. Response:', contactResult);
          // Return success but note the issue
          return {
            success: true,
            contactId: 'created_but_no_id',
            timestamp
          };
        }
      }
      
      // Optional: Add note with message
      if (enableNotes) {
        try {
          await this.addNote({
            contactId,
            body: `Contact form submission: ${formData.message}`
          });
          console.log('✅ Note added successfully');
        } catch (noteError) {
          console.warn('⚠️ Note addition failed, but continuing with contact creation:', noteError);
        }
      }
      
      // Optional: Create opportunity if service interest specified
      let opportunityId: string | undefined;
      if (enableOpportunities && formData.serviceInterest) {
        // Get the first stage of the pipeline (if possible) or use default
        let stageId = 'new'; // Default stage ID that GHL might recognize
        try {
          const pipelineStages = await this.getPipelineStages(this.config.defaultPipelineId);
          if (pipelineStages.success && pipelineStages.data && pipelineStages.data.length > 0) {
            stageId = pipelineStages.data[0].id;
          }
        } catch (error) {
          // If we can't get stages, use default stage
          console.warn('Could not fetch pipeline stages, using default stage "new"');
        }
        
        const opportunity: GHLOpportunity = {
          pipelineId: this.config.defaultPipelineId,
          stageId: stageId,
          name: `${formData.firstName} ${formData.lastName} - ${formData.serviceInterest}`,
          contactId,
          status: 'open',
          source: formData.source
        };
        
        const oppResult = await this.createOpportunity(opportunity);
        if (oppResult.success && oppResult.data) {
          opportunityId = oppResult.data.id;
        }
      }
      
      // Log success event
      this.logCRMEvent({
        type: 'contact_created',
        data: { contactId, opportunityId, formType: 'contact' },
        timestamp,
        source: formData.source
      });
      
      return {
        success: true,
        contactId,
        opportunityId,
        timestamp
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Log error event
      this.logCRMEvent({
        type: 'error_occurred',
        data: { error: errorMessage, formType: 'contact' },
        timestamp,
        source: formData.source
      });
      
      return {
        success: false,
        error: errorMessage,
        timestamp
      };
    }
  }

  async submitNewsletterSignup(signupData: NewsletterSignup): Promise<FormSubmissionResult> {
    const timestamp = new Date().toISOString();
    
    try {
      // Check if contact exists
      const existingContact = await this.searchContacts(signupData.email);
      
      let contactId: string;
      
      if (existingContact.success && existingContact.data && existingContact.data.length > 0) {
        // Update existing contact with newsletter tag - handle v1 API response structure
        const v1ExistingContact = existingContact.data[0] as any;
        contactId = v1ExistingContact.contact?.id || v1ExistingContact.id;
        
        const updateData: Partial<GHLContact> = {
          tags: ['website_lead', 'newsletter_subscriber'],
          customFields: {
            newsletter_interests: signupData.interests?.join(', ')
          }
        };
        
        await this.updateContact(contactId, updateData);
      } else {
        // Create new contact
        const newContact: GHLContact = {
          firstName: signupData.firstName || '',
          lastName: signupData.lastName || '',
          email: signupData.email,
          source: signupData.source,
          tags: ['website_lead', 'newsletter_subscriber'],
          customFields: {
            newsletter_interests: signupData.interests?.join(', ')
          }
        };
        
        const contactResult = await this.createContact(newContact);
        
        if (!contactResult.success || !contactResult.data) {
          return {
            success: false,
            error: contactResult.error || 'Failed to create contact',
            timestamp
          };
        }
        
        // Handle v1 API response structure for newsletter signup
        const v1NewsletterResponse = contactResult.data as any;
        contactId = v1NewsletterResponse.contact?.id || v1NewsletterResponse.id;
      }
      
      // Log success event
      this.logCRMEvent({
        type: 'contact_created',
        data: { contactId, formType: 'newsletter' },
        timestamp,
        source: signupData.source
      });
      
      return {
        success: true,
        contactId,
        timestamp
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      this.logCRMEvent({
        type: 'error_occurred',
        data: { error: errorMessage, formType: 'newsletter' },
        timestamp,
        source: signupData.source
      });
      
      return {
        success: false,
        error: errorMessage,
        timestamp
      };
    }
  }

  // Event logging for analytics
  private logCRMEvent(event: CRMEvent): void {
    // In a real implementation, you might send this to an analytics service
    // or store it in local storage for later analysis
    console.log('CRM Event:', event);
    
    // Could also trigger custom events for tracking
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('crm-event', { detail: event }));
    }
  }

  // Utility methods
  getServiceTypePipeline(serviceType: string): string {
    // Map service types to pipeline IDs
    const pipelineMap: Record<string, string> = {
      'daisy': process.env.REACT_APP_GHL_DAISY_PIPELINE || this.config.defaultPipelineId,
      'daisy_agency': process.env.REACT_APP_GHL_DAISY_AGENCY_PIPELINE || this.config.defaultPipelineId,
      'flourish': process.env.REACT_APP_GHL_FLOURISH_PIPELINE || this.config.defaultPipelineId,
      'ai_consulting': process.env.REACT_APP_GHL_CONSULTING_PIPELINE || this.config.defaultPipelineId,
      'training': process.env.REACT_APP_GHL_TRAINING_PIPELINE || this.config.defaultPipelineId
    };
    
    return pipelineMap[serviceType] || this.config.defaultPipelineId;
  }

  // Get pipelines
  async getPipelines(): Promise<GHLApiResponse<any[]>> {
    return this.apiCall('/pipelines');
  }

  // Get pipeline stages
  async getPipelineStages(pipelineId: string): Promise<GHLApiResponse<any[]>> {
    // For v1 API, try the pipelines endpoint directly
    return this.apiCall(`/pipelines/${pipelineId}/stages`);
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.apiCall('/locations/' + this.config.locationId);
      return response.success;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const ghlService = new GoHighLevelService();
export default ghlService; 